// Functions below lifted from https://stackoverflow.com/a/32257791
function hex (c: string | number) {
  const s = '0123456789abcdef'
  let i = typeof c === 'string' ? parseInt(c) : c
  if (i === 0 || isNaN(i)) { return '00' }
  i = Math.round(Math.min(Math.max(0, i), 255))
  return s.charAt((i - i % 16) / 16) + s.charAt(i % 16)
}

/* Convert an RGB triplet to a hex string */
function RGBToHex (rgb: [number, number, number]): string {
  return '#' + hex(rgb[0]) + hex(rgb[1]) + hex(rgb[2])
}

/* Remove '#' in color hex string */
function trim (s: string) { return (s.charAt(0) === '#') ? s.substring(1, 7) : s }

/* Convert a hex string to an RGB triplet */
function convertToRGB (hex: string) {
  const color = []
  color[0] = parseInt((trim(hex)).substring(0, 2), 16)
  color[1] = parseInt((trim(hex)).substring(2, 4), 16)
  color[2] = parseInt((trim(hex)).substring(4, 6), 16)
  return color
}

/* Inclusive of color start, exclusive of color end */
export function generateColorArray (colorStart: string, colorEnd: string, colorCount: number): string[] {
  // The beginning of your gradient
  const start = convertToRGB(colorStart)

  // The end of your gradient
  const end = convertToRGB(colorEnd)

  // The number of colors to compute
  const len = colorCount

  // Alpha blending amount
  let alpha = 0.0

  const saida = []

  for (let i: number = 0; i < len; i++) {
    alpha += (1.0 / len)
    const c: [number, number, number] = [
      start[0] * alpha + (1 - alpha) * end[0],
      start[1] * alpha + (1 - alpha) * end[1],
      start[2] * alpha + (1 - alpha) * end[2]
    ]
    saida.push(RGBToHex(c))
  }

  return saida
}

export function offWhiteColor (hexColor: string) {
  const [r, g, b] = convertToRGB(hexColor)
  let [h, s, l] = RGBToHSL([r, g, b])
  l = 85
  s += 5
  if (s > 100) s = 100
  const rgb = HSLToRGB([h, s, l])
  const hex = RGBToHex(rgb)
  return hex
}

function RGBToHSL (rgb: [number, number, number]): [number, number, number] {
  // Make r, g, and b fractions of 1
  let [r, g, b] = rgb
  r /= 255
  g /= 255
  b /= 255

  // Find greatest and smallest channel values
  const cmin = Math.min(r, g, b)
  const cmax = Math.max(r, g, b)
  const delta = cmax - cmin
  let h = 0
  let s = 0
  let l = 0

  // Calculate hue
  if (delta === 0) { // No difference
    h = 0
  } else if (cmax === r) { // Red is max
    h = ((g - b) / delta) % 6
  } else if (cmax === g) { // Green is max
    h = (b - r) / delta + 2
  } else { // Blue is max
    h = (r - g) / delta + 4
  }

  h = Math.round(h * 60)

  // Make negative hues positive behind 360°
  if (h < 0) { h += 360 }

  // Calculate lightness
  l = (cmax + cmin) / 2

  // Calculate saturation
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  // Multiply l and s by 100
  s = +(s * 100).toFixed(1)
  l = +(l * 100).toFixed(1)

  return [h, s, l]
}

function HSLToRGB (hsl: [number, number, number]): [number, number, number] {
  let [h, s, l] = hsl
  // Must be fractions of 1
  s /= 100
  l /= 100
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x
  }
  r = Math.round((r + m) * 255)
  g = Math.round((g + m) * 255)
  b = Math.round((b + m) * 255)

  return [r, g, b]
}
